import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";

import API from "util/API";
import { useAuthenticatedQuery } from "hooks/useReactQuery";

import { CSVLink } from "react-csv";
import moment from "moment-timezone";
import { Line } from "react-chartjs-2";
import palette from "theme/palette";

import { LatestSales } from "../../Dashboard/components";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  table: {},
  textLink: {
    textDecoration: 'underline',
  },
  chartContainer: {
    height: 400,
    position: 'relative'
  },
}));

const SalesReport = (props) => {
  const classes = useStyles();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [salesMonthDashboard, setSalesMonthDashboard] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [timezone, setTimezone] = useState('Australia/Sydney');
  const [chartData, setChartData] = useState(null);
  const [isLoadingMonth, setIsLoadingMonth] = useState(false);

  // Generate year options for the past two years
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  // Fetch sales report data using React Query
  const { data: reportData = [], isLoading } = useAuthenticatedQuery(
    ['salesReport', selectedYear],
    async () => {
      const response = await API.get("/salesReport", {
        params: { year: selectedYear }
      });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const fectchTransactionMonthDashboard = async (monthStr) => {
    setIsLoadingMonth(true);
    try {
      const response = await API.get("/salesMonthDashboard", {
        params: { monthStr, yearStr: selectedYear.toString() },
      });
      setSalesMonthDashboard(response.data.rows);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingMonth(false);
    }
  };

  const showMonthDetailReport = (monthStr) => {
    setSelectedMonth(monthStr);
    fectchTransactionMonthDashboard(monthStr);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setSalesMonthDashboard([]); // Clear month dashboard when year changes
    setSelectedMonth(''); // Clear selected month when year changes
    setChartData(null); // Clear chart data when year changes
    setIsLoadingMonth(false); // Clear month loading state when year changes
  };

  // Build chart data when salesMonthDashboard changes
  useEffect(() => {
    if (salesMonthDashboard.length > 0) {
      const labels = salesMonthDashboard.map(item => 
        moment(item.date).tz(timezone).format('DD/MM ddd')
      );
      const salesData = salesMonthDashboard.map(item => 
        item.amount ? Number(item.amount).toFixed(2) : 0
      );

      setChartData({
        labels: labels,
        datasets: [
          {
            label: 'Daily Sales',
            backgroundColor: palette.primary.main,
            borderColor: palette.primary.main,
            data: salesData,
            fill: false,
            tension: 0.4,
          }
        ]
      });
    } else {
      setChartData(null);
    }
  }, [salesMonthDashboard, timezone]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { 
        display: true,
        position: 'top',
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        borderWidth: 1,
        borderColor: palette.divider,
        backgroundColor: palette.white,
        titleColor: palette.text?.primary || '#000',
        bodyColor: palette.text?.secondary || '#666',
        footerColor: palette.text?.secondary || '#666',
        callbacks: {
          label: function(context) {
            return `Sales: $${context.parsed.y}`;
          }
        }
      }
    },
    layout: { padding: 0 },
    scales: {
      x: {
        ticks: {
          color: palette.text?.secondary || '#666'
        },
        grid: {
          display: false,
          drawBorder: false
        }
      },
      y: {
        ticks: {
          color: palette.text?.secondary || '#666',
          beginAtZero: true,
          callback: function(value) {
            return '$' + value;
          }
        },
        grid: {
          borderDash: [2],
          borderDashOffset: [2],
          color: palette.divider,
          drawBorder: false,
        }
      }
    }
  };


  const getRows = () => {
    let rows = [];
    reportData.forEach((item) => {
      const monthString = moment(item.month).format("MM/YYYY");

      rows.push(
        <TableRow key={monthString}>
          <TableCell component="th" scope="row">
          
          <Link href="#" onClick={(e) => showMonthDetailReport(monthString)}>
              {monthString}
            </Link>

          
          </TableCell>
          <TableCell align="right">
            {/* <Link href="#" onClick={e => downloadSales(item.month)} >$ */}
            {item.amount}
            {/* </Link> */}
          </TableCell>
        </TableRow>
      );
    });

    return rows;
  };

  const csvData = () => {
    //console.log('keys', keys);
    let data = [];
    data.push(["month", "Sales"]);

    reportData.forEach((item) => {
      const monthString = moment(item.month).format("MM/YYYY");
      if (item.amount && Number(item.amount)) {
        data.push([monthString, item.amount.toFixed(2)]);
      }
    });

    // console.log('data', data);
    return data;
  };

  const csvMonthData = () => {
    //console.log('keys', keys);
    let data = [];
    data.push(["Date", "Sales"]);

    salesMonthDashboard.forEach((item) => {
      const monthString = moment(item.date).tz(timezone).format("DD/MM/YYYY");
      if (item.amount && Number(item.amount)) {
        data.push([monthString, item.amount.toFixed(2)]);
      }
    });

    // console.log('data', data);
    return data;
  };

  return (
    <div className={classes.root}>
      {isLoading ? <LinearProgress style={{ marginBottom: 8 }} /> : null}
      <Grid container spacing={1}>
        <Grid item lg={8} md={12} xl={9} xs={12}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="year-select-label">Year</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={selectedYear}
                label="Year"
                onChange={handleYearChange}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <CSVLink filename={"sales-report.csv"} data={csvData()}>
              <Typography variant='h5' className={classes.textLink}>Download CSV</Typography>
            </CSVLink>
          </Box>

          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="sales table">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Sales</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{getRows()}</TableBody>
            </Table>
          </TableContainer>
        </Grid>
        {isLoadingMonth && (
          <Grid item lg={8} md={12} xl={9} xs={12}>
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                Loading month details...
              </Typography>
            </Box>
          </Grid>
        )}
        {salesMonthDashboard.length > 0 && selectedMonth && !isLoadingMonth && (
          <Grid item lg={8} md={12} xl={9} xs={12}>
            <Card sx={{ mt: 3 }}>
              <CardHeader
                title={`Daily Sales Breakdown - ${selectedMonth}`}
                subheader={`Total: $${salesMonthDashboard.reduce((sum, item) => sum + (Number(item.amount) || 0), 0).toFixed(2)}`}
              />
              <Divider />
              <CardContent>
                <div className={classes.chartContainer}>
                  {chartData && chartData.labels && chartData.datasets ? (
                    <Line
                      key={`chart-${selectedMonth}`}
                      data={chartData}
                      options={chartOptions}
                    />
                  ) : (
                    <div>Loading chart...</div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Box sx={{ mt: 2 }}>
              <CSVLink 
                filename={`sales-daily-breakdown-${selectedMonth.replace('/', '-')}.csv`}
                data={csvMonthData()}>
                <Typography variant='h5' className={classes.textLink}>
                  Download Daily Sales CSV ({selectedMonth})
                </Typography>
              </CSVLink>
            </Box>

            {/* <LatestSales orderMonthDashboard={salesMonthDashboard} /> */}

          </Grid>
        )} 
      </Grid>
    </div>
  );
};

export default SalesReport;
